/**
 * Aggregated FAQ — single source of truth for /faq page,
 * plus integral FAQPage JSON-LD schema for search engines.
 *
 * Writing rules (per docs/seo-geo-playbook.md §4.2):
 *   - Each answer is a self-contained 80–180 char paragraph.
 *   - Brand canonical terms ONLY: Hotline / Caller / Responder /
 *     OPC / 数字分身 / Marketplace.
 *   - 30+ Q&A pairs, grouped by topic so /faq page can render
 *     accordion-by-section while still emitting one flat FAQPage
 *     schema.
 */

import type { SeoFaqItem } from "./seo-content";

export type FaqGroup = {
  id: string;
  label: string;
  intro: string;
  items: SeoFaqItem[];
};

export const FAQ_GROUPS: FaqGroup[] = [
  {
    id: "what-is",
    label: "CALL ANYTHING 是什么",
    intro: "30 秒理解我们在做的事和不做的事。",
    items: [
      {
        question: "CALL ANYTHING 到底是什么？",
        answer:
          "CALL ANYTHING 是一个让 AI Agent 通过标准化 Hotline 协议调用外部能力的开放体系。一根 Hotline = 一段 Caller↔Responder 通信契约，把身份、计费、审批、可观测、路由全部固化在协议里。",
      },
      {
        question: "「Hotline」是什么意思？",
        answer:
          "Hotline 是协议里的能力单元——把一段私域专长包装成 Agent 可发现、可调用、可结算的产品形态。它既不是 API，也不是 MCP server，也不是 Skill；前三者只是接入面，Hotline 是产品本体。",
      },
      {
        question: "你们和 MCP 是替代关系吗？",
        answer:
          "不是。MCP 关心「Agent runtime 怎么 invoke 一个 server 上的工具」，关心管道；Hotline 关心「超级个体如何把技能转化为可被 Agent 付费调用的产品」，关心经营。同一根 Hotline 可以同时暴露成 MCP server。",
      },
      {
        question: "「OPC」是什么？谁是你们的目标用户？",
        answer:
          "OPC（One-Person Company，超级个体）是把私域专长打包成数字分身、由 Agent 7×24 调用并自动结算的个体。CALL ANYTHING 的第一受众是 OPC（Responder 侧），第二受众是 Agent 团队（Caller 侧）。",
      },
      {
        question: "现在产品到了什么阶段？",
        answer:
          "协议、客户端 SDK、自托管 Platform、Marketplace 与 Console 原型均已开源；公网 Marketplace 处于 preview，欢迎超级个体用 Local Mode 跑通最小闭环、提交 Hotline draft。",
      },
    ],
  },
  {
    id: "vs-others",
    label: "与 MCP / Skill / API / SaaS 的区别",
    intro: "搞清楚边界，才能决定哪种场景用哪种工具。",
    items: [
      {
        question: "Hotline vs MCP server，最大的差别是什么？",
        answer:
          "MCP 解决「Agent runtime 启动一个 server 并 invoke 工具」的连接问题，无身份、无计费、无评分。Hotline 在协议层就把身份、按次结算、版本与公开评分做掉，让供给方可以是个体而不是公司。",
      },
      {
        question: "Hotline vs Agent Skills，最大的差别是什么？",
        answer:
          "Skill 通过 SKILL.md 注入 prompt，源码完全暴露给模型上下文，且不天然带收益。Hotline 把核心算法留在 Responder 本机、只对外暴露契约，调用即结算。Skill 教模型怎么做，Hotline 让 OPC 的能力被模型按次买单。",
      },
      {
        question: "Hotline vs API / SaaS，最大的差别是什么？",
        answer:
          "API/SaaS 假设供给方是公司，需要 Agent 团队为你写 client 代码、按 plan 付费。Hotline 假设供给方是个体，Caller 不需要 client code、按次自动结算、所有 Hotline 共享同一个 result_package 结构。",
      },
      {
        question: "我已经有 MCP server 了，要不要再做一根 Hotline？",
        answer:
          "可以并存。同一根 Hotline 既可以暴露成 MCP server 给 Agent runtime，也可以单独走协议；MCP 接入方式 = 管道，Hotline = 产品本体。把已有 MCP 工具升级为 Hotline 后，就拿到了身份 + 计费 + 评分 + Marketplace 发现。",
      },
      {
        question: "什么场景下不要用 Hotline？",
        answer:
          "如果你只想给一两个 Agent 内部跑写一个临时工具、不在意结算与对外发现，直接写 MCP server 或 Skill 更轻。Hotline 的优势在于「对外作为可被陌生 Agent 付费调用的能力」，更偏经营侧而非接入侧。",
      },
    ],
  },
  {
    id: "opc-business",
    label: "OPC 商业化",
    intro: "把专长变成被 Agent 调用的收入，过程中常被问的几件事。",
    items: [
      {
        question: "我能靠 Hotline 赚到钱吗？",
        answer:
          "Hotline 的商业模式是「按事先约定，做完按约定付」。Caller 同意金额后调用，Responder 服务被调用即累积成点数收入。前提是你的能力对 Agent 有真实价值且没有更便宜的替代。我们当前处在 preview 阶段（首批 Hotline 上架期），Marketplace 内调用免费，让供给侧先把能力打磨好。",
      },
      {
        question: "结算怎么走？我需要接 Stripe 吗？",
        answer:
          "不需要。当前阶段平台运行在「点数」体系内（Call Credit / 呼叫点），不直接经手货币、不接入支付商、不需要绑定银行账户。结算的全部动作发生在协议层——Caller 充值点数、调用扣减、Responder 收入累积成同一种点数。是否接入法币是后续阶段决策，与协议契约无关。",
      },
      {
        question: "我赚的点数能提现成现金吗？",
        answer:
          "当前阶段不开放真实货币提现。点数只在生态内流转——既能被 caller 消费，也能反过来用作你购买其他 OPC 的 Hotline。当前阶段不直接经手法币、不接入支付商，避开了法律 / 合规 / 跨境支付的复杂度。提现路径属于后续阶段，会与法律 / 合规 / 支付商接入一并 evolve；不预先承诺时间表。",
      },
      {
        question: "我同时是 Caller 也是 Responder，账户是分开的吗？",
        answer:
          "不分开。同一 user 账户的 caller 端余额与 responder 端收入合并：你赚的点数立刻就是你能花的余额，不用走两次充值，也不用 transfer。这是为了让点数生态网络的能量靠双向流动维持——OPC 之间互调时摩擦最小。",
      },
      {
        question: "我的核心算法 / Prompt 会被模型看到吗？",
        answer:
          "不会。Responder 只对外暴露 input_schema / output_schema 与 summary，核心逻辑跑在你本机的 supervisor 进程里，对 Caller 与模型完全黑盒。这与 SKILL.md 那种「源码注入 prompt」的方式根本不同。",
      },
      {
        question: "我能同时挂多根 Hotline 吗？",
        answer:
          "可以。一台 supervisor 可以挂任意数量的 Hotline，每根有独立的 ID、版本、定价与统计。你可以把不同专长拆成多根，Agent 通过 catalog 各自发现、各自调用。",
      },
      {
        question: "我没有公司主体也能做 OPC 吗？",
        answer:
          "可以。CALL ANYTHING 的协议层不假设供给方必须是企业；个人身份即可发布 Hotline，结算时按个人收入处理。这正是「One-Person Company」名字的核心含义。",
      },
    ],
  },
  {
    id: "trust-security",
    label: "安全 / 信任 / 责任",
    intro: "Caller 担心被坑，Responder 担心被白嫖；协议怎么处理。",
    items: [
      {
        question: "Caller 怎么知道一根 Hotline 是否可信？",
        answer:
          "Marketplace 上每根 Hotline 公开 responder 身份、版本、调用次数、平均耗时与 Caller 评分；Caller Console 还可以为高风险 Hotline 配置审批策略，把关键调用交给人工 review。",
      },
      {
        question: "Responder 怎么防止恶意调用 / DDoS？",
        answer:
          "Hotline 调用走协议级 token，Responder 可以设置每分钟 / 每小时 / 每 Caller 的限频，并对异常 caller 在 Console 一键拉黑。supervisor 本身也跑在你本机，物理隔离层就是你机器的防火墙。",
      },
      {
        question: "调用失败 / 跑飞了，谁负责？",
        answer:
          "Responder 对 result_package 的内容质量负责；Caller 对自己 Agent 用了 result 之后做的决定负责；CALL ANYTHING 协议保证传输、计费、审批的公平与可观测。三方责任在协议里有明确边界。在协议层，5 类机器可判定的失败（result UNVERIFIED、TIMED_OUT、不可重试 FAILED、Hotline 在 prepared 期间被冻结、平台内容审查拒绝）触发自动全额退款，不需要 Caller 主动申诉。",
      },
      {
        question: "Responder 用 LLM 抽风返回了垃圾结果，是 Caller 自己倒霉吗？",
        answer:
          "不是。result 进入 UNVERIFIED（签名/schema 失败）时协议层自动全额退款，OPC 损失收入；这是 OPC 作为经营者必须承担的成本——「我不管你用什么手段，做完按约定付」的对偶面就是「你用了什么手段，你为它的输出负责」。协议层不区分「主观恶意」和「模型不可控输出」，按客观行为指标统一处理。",
      },
      {
        question: "Hotline 怎么防止洗榜 / 批量上架虚假能力？",
        answer:
          "通过零信任分级（trust_tier）+ 机器化熔断。新 Hotline 即使过审也默认 untrusted 档，单次金额上限受限；运行期持续做声明-实际偏差熔断（pricing drift / SLA drift）和双盲一致性采样。累积可签名指标 + 平台内容安全审查后才能升级到 trusted / verified。任何严重事件直接进 frozen，解冻后 trust_tier 重置为 untrusted——杜绝「冻一次解一次回 verified」刷分循环。",
      },
      {
        question: "数据隐私是怎么处理的？",
        answer:
          "Hotline 默认不在 Marketplace 留存调用 payload，只留 metadata（耗时、状态、计费）。Responder 可在本机 supervisor 配置自己的日志策略；Caller 可在 Caller Console 选择对敏感 Hotline 做端到端加密。",
      },
      {
        question: "你们有 SLA 吗？",
        answer:
          "Marketplace 上每根 Hotline 显示历史可用率与平均耗时；Responder 可以自报 SLA 并由 Marketplace 监测兑现度。我们不集中托管 Hotline，因此不出售统一 SLA，而是把 SLA 信号透明给 Caller 自行选择。",
      },
    ],
  },
  {
    id: "open-source",
    label: "开源与边界",
    intro: "什么是开源的、什么不是、为什么这样划分。",
    items: [
      {
        question: "你们是开源项目吗？",
        answer:
          "协议、客户端 SDK 与可自托管 Platform 全部开源（见 GitHub `delegated-execution-protocol` / `-client` / `-platform-selfhost`）。公网 Marketplace 与官方运营基础设施目前不开源，但可以被任何自托管 Platform 替换。",
      },
      {
        question: "我可以自己跑一个私有 Marketplace 吗？",
        answer:
          "可以。`delegated-execution-platform-selfhost` 仓库就是为团队 / 企业内部自建 Marketplace 与 Console 设计的，OPC 与 Caller 都用同一份 client SDK 接入，仅指向你自己的 Platform endpoint。",
      },
      {
        question: "协议会不会随时改、把我的 Hotline 跑挂？",
        answer:
          "Hotline ID 自带版本号（如 `…workspace-summary.v1`）。协议字段升级走 deprecation window，新版本会和老版本同时跑一段时间，给 Responder 留出迁移窗口。Marketplace 会提前提醒。",
      },
      {
        question: "License 是什么？",
        answer:
          "三个核心仓库均使用 MIT License。商业使用、二次发布、私有化部署均不受限；唯一要求是保留原始版权声明。",
      },
    ],
  },
  {
    id: "engineering",
    label: "工程接入",
    intro: "动手前的几个常见门槛问题。",
    items: [
      {
        question: "本地不接 Platform 也能跑通吗？",
        answer:
          "可以。Local Mode 下 supervisor 同时扮演 Caller 与 Responder，挂上内置 example hotline 即可跑通完整调用链路，5 分钟看到一份真实的 result_package。Quick Start 两篇就是按这个最小闭环写的。",
      },
      {
        question: "现在支持哪些语言 / 运行时？",
        answer:
          "client SDK 当前以 Node.js 为主（process / http 适配器都开源），通过协议层可对接任意语言：Responder 可以用 Python / Go / Rust 写 hotline 内部实现，由 supervisor 通过子进程或 HTTP 拉起。",
      },
      {
        question: "我能把自己的内部工具变成 Hotline 吗？",
        answer:
          "可以。最低成本的接入方式是 process adapter——把已有 CLI 直接包成 Hotline，input_schema 写参数、output_schema 写 stdout 解析；http adapter 适合内部已有 service。两种方式 30 分钟内能跑通。",
      },
      {
        question: "调用是同步还是异步？",
        answer:
          "协议同时支持。Caller 发起 request 后可以选择阻塞等 result 或拿 request_id 异步轮询；长任务建议异步，由 supervisor 写状态、Caller 用 Console 或轮询查询完成情况。",
      },
      {
        question: "我能在 Hotline 内部继续调用别人的 Hotline 吗？",
        answer:
          "可以。Responder 同时可以是 Caller，supervisor 提供同样的 catalog 与 request API。这让 Hotline 可以组合：一根上层 Hotline 在内部调度多根下层 Hotline，Marketplace 上仍然只暴露顶层契约。",
      },
    ],
  },
  {
    id: "marketplace-ops",
    label: "Marketplace 运营",
    intro: "上架、下架、版本、评分都怎么走。",
    items: [
      {
        question: "怎么把一根 Hotline 提交到 Marketplace？",
        answer:
          "在本机用 supervisor 把 Hotline 跑通后，通过 Responder Console（或 CLI）提交 template bundle 到 Platform，包含 summary、input_schema、output_schema、示例与风险说明。审核通过即出现在公网 Marketplace。",
      },
      {
        question: "Hotline 审核标准是什么？",
        answer:
          "三条：能跑（example call 必须返回结构合法的 result_package）、能讲清（summary / input_schema 没歧义，Caller 能在 30 秒内判断是否需要）、不违规（不涉及非法 / 高风险类目）。",
      },
      {
        question: "我能下架自己的 Hotline 吗？",
        answer:
          "可以。Responder Console 一键下架后，Marketplace 不再发现，但已对接的 Caller 仍能在 deprecation window 内继续调用，避免上线 Agent 突然失效。窗口结束后老版本彻底关闭。",
      },
      {
        question: "Hotline 有评分吗？",
        answer:
          "有。Caller 在使用后可以为 result 质量与稳定性打分，Marketplace 公开累计分与最近 30 天分。评分会反向影响搜索排序，鼓励 Responder 持续优化。",
      },
      {
        question: "我能把同一根 Hotline 发不同价格吗？",
        answer:
          "可以。同一个 namespace 下你可以发布多个 hotline-name 或多个 version，分别定不同价（如基础版 vs 专家版）。Caller 根据自己 Agent 的预算与质量需求挑选。",
      },
    ],
  },
];

/** Flat list of all FAQ items (for FAQPage JSON-LD schema). */
export const ALL_FAQS: SeoFaqItem[] = FAQ_GROUPS.flatMap((g) => g.items);
